import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, RefreshCw, Search, Shield, UserRound } from 'lucide-react';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import {
  ActionButton,
  DataListTable,
  ErrorState,
  PageHeader,
  PaginationControls,
  SectionCard,
  StatusBadge,
  Toolbar,
  Field,
  TextInput,
  SelectInput,
  formatDateOnly,
  formatNumber,
} from '../../components/superadmin/SuperAdminUI';

const DEFAULT_FILTERS = {
  q: '',
  status: '',
  sort: 'created_at',
  order: 'desc',
};

const SuperAdminShopsPage = () => {
  const { getAdmins } = useSuperadminAuth();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draftSearch, setDraftSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [admins, setAdmins] = useState([]);

  const loadDirectory = useCallback(async (page = 1, nextFilters = filters) => {
    try {
      setLoading(true);
      setError('');
      const response = await getAdmins({
        page,
        pageSize: pagination.pageSize,
        ...nextFilters,
      });

      if (!response.ok) {
        throw new Error(response.error || 'Failed to load admin explorer');
      }

      setAdmins(response.data || []);
      setPagination(response.pagination || { page: 1, pageSize: 20, total: 0, totalPages: 0 });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load admin explorer');
    } finally {
      setLoading(false);
    }
  }, [filters, getAdmins, pagination.pageSize]);

  useEffect(() => {
    loadDirectory(1, filters);
  }, [filters, loadDirectory]);

  const handleFilterChange = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleCombinedFilters = (updates) => {
    setFilters((current) => ({ ...current, ...updates }));
  };

  const rows = useMemo(() => {
    return admins.map((admin) => ({
      ...admin,
      primaryShop: admin.shops?.[0] || null,
    }));
  }, [admins]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Core feature"
        title="Admin Explorer"
        description="Search by admin name, admin email, or shop name, then drill into the selected shop without leaving the Super Admin workspace."
        action={
          <ActionButton variant="accent" size="md" onClick={() => loadDirectory(pagination.page, filters)}>
            <span className="inline-flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </span>
          </ActionButton>
        }
      />

      {error ? <ErrorState error={error} onRetry={() => loadDirectory(pagination.page, filters)} /> : null}

      <Toolbar>
        <div className="grid flex-1 gap-3 md:grid-cols-[minmax(0,1.4fr)_220px_220px]">
          <Field label="Search admins or shops">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <TextInput
                value={draftSearch}
                onChange={(event) => setDraftSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleFilterChange('q', draftSearch.trim());
                  }
                }}
                placeholder="Name, email, or shop"
                className="pl-11"
              />
            </div>
          </Field>
          <Field label="Shop status">
            <SelectInput value={filters.status} onChange={(event) => handleFilterChange('status', event.target.value)}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </SelectInput>
          </Field>
          <Field label="Sort order">
            <SelectInput
              value={`${filters.sort}:${filters.order}`}
              onChange={(event) => {
                const [sort, order] = event.target.value.split(':');
                handleCombinedFilters({ sort, order });
              }}
            >
              <option value="created_at:desc">Newest first</option>
              <option value="created_at:asc">Oldest first</option>
              <option value="name:asc">Admin name A-Z</option>
              <option value="name:desc">Admin name Z-A</option>
            </SelectInput>
          </Field>
        </div>
        <ActionButton variant="secondary" size="md" onClick={() => handleFilterChange('q', draftSearch.trim())}>
          Search
        </ActionButton>
      </Toolbar>

      <SectionCard
        title="Shops and admins"
        subtitle={`${formatNumber(pagination.total)} matching records`}
        icon={<Building2 className="h-5 w-5" style={{ color: '#7d6633' }} />}
      >
        <div className="space-y-4">
          <DataListTable
            columns={[
              {
                key: 'admin',
                label: 'Admin',
                render: (row) => (
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-zinc-900 dark:text-zinc-100">
                      <UserRound className="h-4 w-4 text-zinc-400" />
                      <span>{row.name}</span>
                    </div>
                    <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{row.email}</div>
                  </div>
                ),
              },
              {
                key: 'shops',
                label: 'Shop',
                render: (row) => (
                  <div className="space-y-1">
                    <div className="font-medium text-zinc-900 dark:text-zinc-100">{row.primaryShop?.name || 'No linked shop'}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">/{row.primaryShop?.slug || 'n/a'}</div>
                  </div>
                ),
              },
              {
                key: 'status',
                label: 'Status',
                render: (row) => (
                  <div className="space-y-2">
                    <StatusBadge status={row.primaryShop?.subscription_status || 'unknown'} variant={row.primaryShop?.subscription_status === 'active' ? 'success' : row.primaryShop?.subscription_status === 'trial' ? 'warning' : 'default'} />
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {row.is_disabled ? 'Disabled admin account' : 'Admin account active'}
                    </div>
                  </div>
                ),
              },
              {
                key: 'details',
                label: 'Details',
                render: (row) => (
                  <div className="space-y-1 text-xs text-zinc-500 dark:text-zinc-400">
                    <div className="inline-flex items-center gap-1">
                      <Shield className="h-3.5 w-3.5" />
                      <span>{row.shops?.length || 0} linked shop(s)</span>
                    </div>
                    <div>Created {formatDateOnly(row.created_at)}</div>
                  </div>
                ),
              },
              {
                key: 'actions',
                label: 'Open',
                render: (row) =>
                  row.primaryShop ? (
                    <Link
                      to={`/superadmin/shops/${row.primaryShop.id}/overview`}
                      className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-[rgba(125,102,51,0.08)] px-3 py-2 text-sm font-semibold text-[#7d6633] transition hover:bg-[rgba(125,102,51,0.14)]"
                    >
                      <span>Open shop</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : (
                    <span className="text-xs text-zinc-400">Unavailable</span>
                  ),
              },
            ]}
            rows={loading ? [] : rows}
            emptyTitle={loading ? 'Loading explorer...' : 'No matching shops found'}
            emptyDescription={loading ? 'Fetching the admin explorer from the live Super Admin APIs.' : 'Try broadening the search or clearing the current filters.'}
          />

          <PaginationControls pagination={pagination} onPageChange={(page) => loadDirectory(page, filters)} />
        </div>
      </SectionCard>
    </div>
  );
};

export default SuperAdminShopsPage;
